import React, { useState } from "react";
import { Download, FileText, CheckCircle2, PenLine } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { http, formatApiErrorDetail } from "../lib/api";
import SignatureModal from "../components/SignatureModal";
import HoneypotField from "../components/HoneypotField";

const empty = {
  razon_social: "",
  rut_comunidad: "",
  direccion: "",
  comuna_ciudad: "",
  representante_nombre: "",
  representante_rut: "",
  representante_cargo: "",
  representante_telefono: "",
  representante_email: "",
  fecha: new Date().toISOString().slice(0, 10),
};

export default function CartaAsignacion() {
  const [d, setD] = useState(empty);
  const [signOpen, setSignOpen] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signedBlob, setSignedBlob] = useState(null);
  const [signedId, setSignedId] = useState(null);
  const [firma, setFirma] = useState(null); // { tipo, imagen_base64, nombre_escrito? }
  const [website, setWebsite] = useState(""); // honeypot
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));

  const required = [
    "razon_social","rut_comunidad","direccion","comuna_ciudad",
    "representante_nombre","representante_rut","representante_cargo",
    "representante_telefono","representante_email",
  ];
  const complete = required.every((k) => d[k]?.trim());

  // Mantenemos la fecha siempre actualizada al día actual (auto)
  React.useEffect(() => {
    setD((p) => ({ ...p, fecha: new Date().toISOString().slice(0, 10) }));
  }, []);

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleSignConfirm = async (payload) => {
    setSignOpen(false);
    setSigning(true);
    try {
      const body = {
        ...d,
        firma_tipo: payload.tipo,
        firma_imagen_base64: payload.imagen_base64,
        firma_nombre_escrito: payload.nombre_escrito || null,
        website,
      };
      const res = await http.post("/cartas-asignacion/firmar", body, { responseType: "blob" });
      const cartaId = res.headers["x-carta-id"] || res.headers["X-Carta-Id"] || null;
      setSignedBlob(res.data);
      setSignedId(cartaId);
      setFirma(payload);
      // Descarga automática
      downloadBlob(res.data, `carta-asignacion-firmada-${(d.razon_social || "comunidad").replace(/\s+/g, "_")}.pdf`);
      toast.success("Carta firmada y descargada");
    } catch (e) {
      toast.error("Error al firmar", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally {
      setSigning(false);
    }
  };

  const reDownload = () => {
    if (!signedBlob) return;
    downloadBlob(signedBlob, `carta-asignacion-firmada-${(d.razon_social || "comunidad").replace(/\s+/g, "_")}.pdf`);
  };

  return (
    <Layout>
      <div className="container-x py-12 md:py-16">
        <div className="max-w-3xl mb-10">
          <div className="flex items-center gap-2 mb-3">
            <FileText size={18} style={{ color: "var(--brand-accent)" }} />
            <span className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: "var(--brand-accent)" }}>
              Documento legal
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4" style={{ fontFamily: "Manrope" }}>
            Carta de Asignación de Corredor
          </h1>
          <p className="text-lg" style={{ color: "var(--text-muted)" }}>
            Completa los datos, firma el documento haciendo clic en firmar y con esto nos asignas como corredor para
            mejorar tu póliza y activarla.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* FORM */}
          <div className="space-y-6 bg-white border rounded-xl p-6 md:p-8" style={{ borderColor: "var(--border-subtle)" }} data-testid="carta-form">
            <HoneypotField value={website} onChange={setWebsite} />
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold mb-4 pb-2 border-b" style={{ color: "var(--brand-navy)", borderColor: "var(--border-subtle)" }}>
                Datos de la comunidad
              </div>
              <div className="space-y-4">
                <Fld label="Razón social" value={d.razon_social} onChange={(v) => set("razon_social", v)} testid="carta-razon-social"/>
                <Fld label="RUT de la comunidad" value={d.rut_comunidad} onChange={(v) => set("rut_comunidad", v)} placeholder="76.123.456-9" testid="carta-rut"/>
                <Fld label="Dirección" value={d.direccion} onChange={(v) => set("direccion", v)} testid="carta-direccion"/>
                <Fld label="Comuna / Ciudad" value={d.comuna_ciudad} onChange={(v) => set("comuna_ciudad", v)} testid="carta-comuna"/>
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold mb-4 pb-2 border-b" style={{ color: "var(--brand-navy)", borderColor: "var(--border-subtle)" }}>
                Representante que firma
              </div>
              <div className="space-y-4">
                <Fld label="Nombre completo" value={d.representante_nombre} onChange={(v) => set("representante_nombre", v)} testid="carta-rep-nombre"/>
                <div className="grid md:grid-cols-2 gap-4">
                  <Fld label="RUT" value={d.representante_rut} onChange={(v) => set("representante_rut", v)}/>
                  <div>
                    <label className="field-label">Cargo</label>
                    <select
                      className="field-input"
                      value={d.representante_cargo}
                      onChange={(e) => set("representante_cargo", e.target.value)}
                      data-testid="carta-rep-cargo"
                    >
                      <option value="">Seleccionar</option>
                      <option value="Administrador">Administrador</option>
                      <option value="Comité">Comité</option>
                    </select>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Fld label="Teléfono" value={d.representante_telefono} onChange={(v) => set("representante_telefono", v)}/>
                  <Fld label="Email" value={d.representante_email} onChange={(v) => set("representante_email", v)} type="email"/>
                </div>
                <p className="text-[12px]" style={{ color: "var(--text-muted)" }}>
                  La fecha del documento se completa automáticamente con la fecha de descarga.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              {!signedBlob ? (
                <button className="btn-primary justify-center w-full" onClick={() => setSignOpen(true)} disabled={!complete || signing} data-testid="btn-firmar">
                  <PenLine size={16}/> {signing ? "Firmando..." : "Firmar"}
                </button>
              ) : (
                <>
                  <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-[13px] font-semibold flex-1 justify-center" style={{ background: "rgba(34, 197, 94, 0.08)", color: "#15803d", border: "1px solid rgba(34, 197, 94, 0.25)" }}>
                    <CheckCircle2 size={15}/> Carta firmada y guardada
                  </div>
                  <button className="btn-outline justify-center" onClick={reDownload} data-testid="btn-redescargar">
                    <Download size={16}/> Descargar copia
                  </button>
                </>
              )}
            </div>
            {!complete && !signedBlob && (
              <p className="text-[12px]" style={{ color: "var(--text-muted)" }}>
                Completa todos los campos para habilitar la firma.
              </p>
            )}
          </div>

          {/* PREVIEW */}
          <div className="lg:sticky lg:top-24 h-fit">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold mb-3" style={{ color: "var(--text-muted)" }}>
              Vista previa
            </div>
            <div className="bg-white border rounded-xl p-8 md:p-10 shadow-sm text-[13px] leading-relaxed" style={{ borderColor: "var(--border-subtle)", minHeight: 640 }} data-testid="carta-preview">
              {/* Header — logo centrado */}
              <div className="flex justify-center mb-3">
                <img
                  src="https://customer-assets.emergentagent.com/job_cotiza-ahora/artifacts/8hb3rbcf_Captura_de_pantalla_2026-05-13_a_la_s__17.02.49-removebg-preview.png"
                  alt="Mi Comunidad Segura"
                  className="h-10 w-auto"
                  draggable={false}
                />
              </div>
              <div className="border-t mb-5" style={{ borderColor: "var(--border-subtle)" }}></div>

              {/* Título centrado */}
              <div className="text-center mb-6">
                <div className="text-[9px] uppercase tracking-[0.2em] font-bold mb-1.5" style={{ color: "var(--brand-accent)" }}>
                  Documento legal
                </div>
                <h2 className="text-xl md:text-2xl font-extrabold tracking-tight mb-1.5" style={{ fontFamily: "Manrope", color: "var(--brand-navy)" }}>
                  Carta de Asignación de Corredor de Seguros
                </h2>
                <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                  Autorización para gestión y cotización de seguros de copropiedad
                </p>
                <p className="text-[11px] font-semibold mt-2" style={{ color: "var(--text-muted)" }}>
                  Santiago de Chile · {formatFechaEs(d.fecha)}
                </p>
              </div>

              {/* Tabla — datos de la comunidad */}
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold mb-2.5" style={{ color: "var(--brand-navy)", fontFamily: "Manrope" }}>
                Datos de la comunidad
              </div>
              <Row label="Razón social" value={d.razon_social} />
              <Row label="RUT" value={d.rut_comunidad} />
              <Row label="Dirección" value={d.direccion} />
              <Row label="Comuna / Ciudad" value={d.comuna_ciudad} />

              <div className="mt-5 mb-2.5 text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: "var(--brand-navy)", fontFamily: "Manrope" }}>
                Representante que firma
              </div>
              <Row label="Nombre" value={d.representante_nombre} />
              <Row label="RUT" value={d.representante_rut} />
              <Row label="Cargo" value={d.representante_cargo} />
              <Row label="Teléfono" value={d.representante_telefono} />
              <Row label="Email" value={d.representante_email} />

              <div className="border-t my-5" style={{ borderColor: "var(--border-subtle)" }}></div>

              {/* Cuerpo */}
              <p className="text-justify">
                Por medio del presente documento, la comunidad individualizada más arriba <strong>autoriza y designa</strong> a{" "}
                <strong>Marchant &amp; Bravo Corredores de Seguros SpA</strong>, RUT <strong>77.963.303-9</strong>, registrados en la{" "}
                <strong>CMF bajo el código 9860</strong>, para que actúe como su corredor en la intermediación, cotización,
                contratación y renovación de pólizas de seguros relacionadas con la copropiedad.
              </p>
              <p className="mt-3 text-justify">
                Esta asignación comprende, sin limitarse a: seguros de incendio, sismo y daños a bienes comunes; responsabilidad
                civil y D&amp;O; seguros de vida y accidentes OS10; y cualquier otra póliza que la comunidad decida contratar.
              </p>
              <p className="mt-3 text-[12px]" style={{ color: "var(--text-muted)" }}>
                Vigencia: 12 meses renovables. Revocable por escrito a Lucas@micomunidadsegura.cl.
              </p>

              {/* Firma — centrada */}
              <div className="mt-12 flex justify-center">
                <div className="w-full max-w-[280px] text-center text-[11px]">
                  <div className="flex items-end justify-center mb-1" style={{ minHeight: 56 }}>
                    {firma?.imagen_base64 ? (
                      <img src={`data:image/png;base64,${firma.imagen_base64}`} alt="firma" className="max-h-14 object-contain" />
                    ) : null}
                  </div>
                  <div className="border-t" style={{ borderColor: "var(--brand-navy)", borderTopWidth: 1.5 }}></div>
                  <div className="font-bold mt-2" style={{ fontFamily: "Manrope", color: "var(--brand-navy)" }}>
                    Firma representante de la comunidad
                  </div>
                  <div className="mt-0.5" style={{ color: "var(--text-muted)" }}>
                    {firma ? `${d.representante_nombre} · ${d.representante_rut} · ${d.representante_cargo}` : "Nombre, RUT y cargo"}
                  </div>
                </div>
              </div>

              <div className="border-t mt-8 pt-4 text-center text-[10px]" style={{ borderColor: "var(--border-subtle)", color: "var(--text-soft)" }}>
                Marchant &amp; Bravo Corredores de Seguros SpA · RUT 77.963.303-9<br/>
                Corredora de Seguros regulada por la CMF (código 9860) · Lucas@micomunidadsegura.cl
              </div>
            </div>
          </div>
        </div>
      </div>

      <SignatureModal
        open={signOpen}
        onClose={() => setSignOpen(false)}
        defaultName={d.representante_nombre}
        onConfirm={handleSignConfirm}
      />
    </Layout>
  );
}

const Fld = ({ label, value, onChange, placeholder, type = "text", testid }) => (
  <div>
    <label className="field-label">{label}</label>
    <input className="field-input" type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}/>
  </div>
);

const MESES_ES = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
function formatFechaEs(iso) {
  try {
    const [y, m, d] = (iso || new Date().toISOString().slice(0, 10)).split("-").map(Number);
    return `${d} de ${MESES_ES[m - 1]} de ${y}`;
  } catch {
    return "";
  }
}

const Row = ({ label, value }) => (
  <div className="flex gap-3 py-1.5 border-b text-[12px]" style={{ borderColor: "var(--border-subtle)" }}>
    <span className="font-bold w-32 flex-shrink-0" style={{ color: "var(--text-muted)", fontFamily: "Manrope" }}>{label}</span>
    <span className="flex-1">{value || <span style={{ color: "#bbb" }}>—</span>}</span>
  </div>
);
